import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    ErrorText, Text, Toast,
    ImageCropModal, VideoCropModal,
    VideoBottomControls, VideoContainer, VideoControlsBar,
    ControlButton, CustomRange, PauseIcon,
    PlayIcon, formatTime, VolumeIcon,
    FullscreenIcon, PortalFormLabel
} from "src/components";
import styled from "styled-components";
import {
    ErrorList,
    ErrorStrings,
    validImageTypes,
    validVideoTypes
} from "../../components/Enums";
import { RemoveMediaButton } from "src/scenes/BodyAndMind/components/style";
import CloseIcon from "src/img/CloseButton.png";
import { useVideoPlayer } from "src/scenes/BodyAndMind/hooks/VideoPreview";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const BrowseFileButton = styled.div`
    width: 140px;
    height: 37.5px;
    background-color: #000;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 7px;
    color: #fff;
    text-align: center;
    align-items: center;
    display: flex;
    justify-content: center;
    font-weight: 400;
    font-size: 0.875rem;
    font-family: "Manrope", "Helvetica", sans-serif;
`;

const ClickableDivBox = styled.div`
    position: relative;
    width: 364px;
    height: 205px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #E9EBEF;
    border-radius: 15px;
    background-color: #F8F8F8;
    cursor: pointer;
    @media (max-width: 600px) {
        max-width: 220px;
        height: 150px;
    };
`;

const ImageBoxParent = styled.div`
    display: flex;
    position: relative;
    align-items: end;
    justify-content: center;
    @media (max-width: 600px) {
        max-width: 220px;
        height: 150px;
    };
`;

const ImageBox = styled.img`
    width: 364px;
    height: 205px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #E0E0E0;
    border-radius: 15px;
    background-color: #E8E8E8;
    cursor: pointer;
    object-fit: cover;
    object-position: center;
    @media (max-width: 600px) {
        max-width: 220px;
        height: 150px;
    };
`;

const AdPrimaryImageUpload = ({
    setFormPrimaryAsset,
    setImageChanged,
    inputErrors,
    asset,
    setFormPrimaryAssetType,
    assetType
}) => {
    const [previewUrl, setPreviewUrl] = useState(null); 
    const [fileTypeOfPreview, setFileTypeOfPreview] = useState(assetType || null);
    const [isCroppingImage, setIsCroppingImage] = useState(false);
    const [isCroppingVideo, setIsCroppingVideo] = useState(false);

    const fileInputRef = useRef(null);
    const objectUrlRef = useRef(null);

    const {
        videoRef,
        containerRef,
        muted,
        currentTime,
        duration,
        isPlaying,
        isHovered,
        setIsHovered,
        togglePlayPause,
        cropStyles,
        useCrop: videoUseCrop,
        toggleMute,
        toggleFullscreen,
        handleProgressChange,
        progressValue,
        validTrimStart
    } = useVideoPlayer({
        videoSrc: fileTypeOfPreview === "video" ? previewUrl : null,
        trimTimeStart: asset?.trimStart,
        trimTimeEnd: asset?.trimEnd,
        crop: asset?.crop
    });

    const primaryAssetError = inputErrors
        .filter(err => err === ErrorList.EMPTY_PRIMARY_ASSET)
        .map(err => ErrorStrings[err]);

    // Cleanup any objectURL when component unmount
    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);

    // Update preview when asset.primaryAssetBlob changes
    useEffect(() => {
        if (asset.primaryAssetBlob) {
            setPreviewUrl(asset.primaryAssetBlob);
            setFileTypeOfPreview(assetType || "image");
        }
    }, [asset.primaryAssetBlob, assetType]);

    // Handle file select
    const handleFileSelect = useCallback((file) => {
        if (!file) return;
        const { type } = file;

        if (validImageTypes.includes(type)) {
            const imageUrl = URL.createObjectURL(file);
            objectUrlRef.current = imageUrl;
            setPreviewUrl(imageUrl);
            setFileTypeOfPreview("image");

            setIsCroppingImage(true);
            setIsCroppingVideo(false);

        } else if (validVideoTypes.includes(type)) {
            const videoUrl = URL.createObjectURL(file);
            objectUrlRef.current = videoUrl;
            setPreviewUrl(videoUrl);
            setFileTypeOfPreview("video");

            setIsCroppingVideo(true);
            setIsCroppingImage(false);

        } else {
            Toast.error("Unsupported file type. Images: JPG, PNG. Videos: MP4.");
        }
    }, []);

    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
            // reset input so selecting same file again triggers change
            e.target.value = "";
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dtFiles = e.dataTransfer.files;
        if (dtFiles && dtFiles[0]) {
            handleFileSelect(dtFiles[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleRemove = (e) => {
        e?.stopPropagation();
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        setPreviewUrl(null);
        setFileTypeOfPreview(null);
        setIsCroppingImage(false);
        setIsCroppingVideo(false);

        setFormPrimaryAsset({});
        setImageChanged(true);
        setFormPrimaryAssetType(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
        }
    };

    // When cropping of image completes
    const handleImageCropComplete = (croppedFile) => {
        if (!croppedFile) {
            setIsCroppingImage(false);
            return;
        }
        // cleanup old
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        // Create new preview
        const newUrl = URL.createObjectURL(croppedFile);
        objectUrlRef.current = newUrl;
        setPreviewUrl(newUrl);
        setFileTypeOfPreview("image");
        setFormPrimaryAsset(prev => ({
            ...prev,
            primaryAssetFile: croppedFile,
            primaryAssetBlob: newUrl
        }));
        setFormPrimaryAssetType("image");
        setImageChanged(true);
        setIsCroppingImage(false);
    };

    // When cropping / trimming of video completes
    const handleVideoCropComplete = (croppedFile, crop, trimStart, trimEnd, aspectRatioLabel) => {
        if (!croppedFile) {
            setIsCroppingVideo(false);
            return;
        }
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        const newUrl = URL.createObjectURL(croppedFile);
        objectUrlRef.current = newUrl;
        setPreviewUrl(newUrl);
        setFileTypeOfPreview("video");
        setFormPrimaryAsset(prev => ({
            ...prev,
            primaryAssetFile: croppedFile,
            primaryAssetBlob: newUrl,
            crop,
            trimStart,
            trimEnd,
            aspectRatio: aspectRatioLabel
        }));
        setFormPrimaryAssetType("video");
        setImageChanged(true);
        setIsCroppingVideo(false);
    };

    // Render preview for image
    const renderImagePreview = () => (
        <ImageBoxParent>
            <ImageBox src={previewUrl} alt="Preview" />
        </ImageBoxParent>
    );

    // Render preview for video
    const renderVideoPreview = () => (
        <VideoContainer
            ref={containerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                width: "364px",
                height: "205px",
                borderRadius: "15px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#E8E8E8",
                boxShadow: "0px 5px 5px #00000026",
                overflow: videoUseCrop ? "hidden" : "visible",
                position: "relative",
                cursor: isPlaying && !isHovered ? "none" : "default"
            }}
        >
            {videoUseCrop ? (
                <>
                    <div style={{
                        width: "364px",
                        height: "205px",
                        borderRadius: "15px",
                        overflow: "hidden",
                        position: "relative"
                    }}>
                        <video
                            ref={videoRef}
                            muted={muted}
                            controls={false}
                            style={cropStyles}
                        >
                            <source src={previewUrl} type="video/mp4" />Your browser does not support the video tag.
                        </video>
                    </div>
                    <VideoBottomControls
                        show={!isPlaying || isHovered}
                        onClick={e => e.stopPropagation()}
                    >
                        <VideoControlsBar interactive>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                <ControlButton onClick={togglePlayPause}>
                                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                </ControlButton>
                                <span style={{ fontSize: "0.85rem", fontFamily: "Manrope, sans-serif" }}>
                                    {formatTime(Math.max(0, currentTime - validTrimStart))} / {formatTime(duration)}
                                </span>
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <ControlButton onClick={toggleMute}>
                                    <VolumeIcon muted={muted} />
                                </ControlButton>
                                <ControlButton onClick={toggleFullscreen}>
                                    <FullscreenIcon />
                                </ControlButton>
                            </div>
                        </VideoControlsBar>
                        <CustomRange
                            value={progressValue}
                            max={duration}
                            min={0}
                            step="0.1"
                            onChange={handleProgressChange}
                            onClick={e => e.stopPropagation()}
                        />
                    </VideoBottomControls>
                </>
            ) : (
                <video
                    ref={videoRef}
                    muted={muted}
                    controls
                    style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "15px",
                        objectFit: "cover"
                    }}
                >
                    <source src={previewUrl} type="video/mp4" />Your browser does not support the video tag.
                </video>
            )}
        </VideoContainer>
    );

    return (
        <div>
            <PortalFormLabel>
                Upload Primary Asset
            </PortalFormLabel>
            <Text size="0.938rem" color="#612684" align="left" style={{ wrap: "auto" }}> Click browse file to upload your image or video.
            </Text>
            <Text
                size="0.875rem"
                color="#808080"
                align="left"
                style={{ wrap: "auto", maxHeight: "48px", maxWidth: "290px" }}
            > Supported formats: JPG, PNG, MP4. Aspect Ratio 16:9 - 1920px x 1080px
            </Text>

            <input
                type="file"
                accept=".png,.jpg,.jpeg, .mp4"
                ref={fileInputRef}
                onChange={handleInputChange}
                style={{ display: "none" }}
            />
            <ClickableDivBox
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                { previewUrl ? (
                    fileTypeOfPreview === "image"
                        ? renderImagePreview()
                        : renderVideoPreview()
                ) : (
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "205px"
                    }}>
                        <BrowseFileButton>Browse File</BrowseFileButton>
                    </div>
                )
                }

                { previewUrl && (
                    <RemoveMediaButton
                        type="button"
                        onClick={handleRemove}
                        style={{ position: "absolute", top: "-8px" }}
                    >
                        <img src={CloseIcon} alt="close button" width={24} height={24} />
                    </RemoveMediaButton>
                )}

            </ClickableDivBox>

            { primaryAssetError.length > 0 && (
                <ErrorText errorTextAlign="left">{ primaryAssetError }</ErrorText>
            )}

            { isCroppingImage && previewUrl && fileTypeOfPreview === "image" && (
                <ImageCropModal
                    imageSrc={previewUrl}
                    isOpen={true}
                    onClose={(shouldClear) => {
                        setIsCroppingImage(false);
                        if (shouldClear) {
                            handleRemove();
                        }
                    }}
                    onCropComplete={handleImageCropComplete}
                    aspectRatios={[
                        { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN }
                    ]}
                    allowedOrientations={["landscape"]}
                    lockedAspectRatio={undefined}
                />
            )}

            { isCroppingVideo && previewUrl && fileTypeOfPreview === "video" && (
                <VideoCropModal
                    videoSrc={previewUrl}
                    isOpen={true}
                    onClose={(shouldClear = false) => {
                        setIsCroppingVideo(false);
                        if (shouldClear) {
                            handleRemove();
                        }
                    }}
                    onCropComplete={handleVideoCropComplete}
                    aspectRatios={[
                        { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN }
                    ]}
                    allowedOrientations={["landscape"]}
                    lockedAspectRatio={undefined}
                />
            )}
        </div>
    );
};

export default AdPrimaryImageUpload;
